/**
 * PATH: packages/email-hub/src/feature/lib/markupCanvas.ts
 * FILE_ID: EMAIL-HUB-LIB-MARKUP-001
 * PURPOSE: Pure drawing primitives for the markup drawer canvas.
 *          Each function takes a 2D context plus a typed shape and
 *          renders it. No React, no DOM lookups, no global state —
 *          which means the markup drawer can swap underlying canvas
 *          implementations or unit-test rendering without spinning
 *          up a real browser.
 * WHY:     Operator request 2026-05-09: "shortcut key to open
 *          copied clipboard in a right drawer sense so I can mark
 *          up the image if required". To stay under the 300-LOC
 *          file limit and avoid pulling in fabric.js / tldraw / a
 *          200kb annotation lib for what is essentially "draw a
 *          red arrow on a screenshot", we hand-roll the four tools
 *          the operator actually asked for: arrow, rectangle, free
 *          freehand, and text label. Anything richer can ship in a
 *          v2 once we see how the workflow lands.
 * FLOW:
 *   Background image → ctx.drawImage()
 *   For each Stroke in order:
 *     ┌─────────────┐   ┌──────────────────┐   ┌──────────────┐
 *     │ Stroke type │ → │ render<T>()      │ → │  pixels      │
 *     │ + points    │   │ (tool-specific)  │   │  on canvas   │
 *     └─────────────┘   └──────────────────┘   └──────────────┘
 * RELATED:
 *   - components/MarkupDrawer.tsx
 *   - hooks/useGlobalMarkupShortcut.ts
 */

export type MarkupTool =
  | 'select'   // pick + drag (and double-click to edit) an existing mark
  | 'arrow'
  | 'rect'
  | 'measure'  // CAD-style dimension line: double arrow + typed label
  | 'freehand'
  | 'text'

export interface Point {
  x: number
  y: number
}

export interface ArrowStroke {
  type: 'arrow'
  start: Point
  end: Point
  color: string
  width: number
}

export interface RectStroke {
  type: 'rect'
  start: Point
  end: Point
  color: string
  width: number
}

export interface FreehandStroke {
  type: 'freehand'
  points: Point[]
  color: string
  width: number
}

export interface TextStroke {
  type: 'text'
  position: Point
  text: string
  color: string
  /** Font size in CSS pixels at the canvas's intrinsic resolution. */
  fontSize: number
}

/** A CAD-style dimension: a line with an arrowhead at BOTH ends, short
 *  perpendicular witness ticks, and a typed label (e.g. "2400mm") drawn
 *  centred on the line and rotated to follow it. There's no real-world
 *  scale — the operator types the measurement, exactly like dimensioning
 *  a drawing by hand. */
export interface MeasureStroke {
  type: 'measure'
  start: Point
  end: Point
  /** What the operator typed over the dimension (may be empty). */
  label: string
  color: string
  width: number
  fontSize: number
}

export type Stroke =
  | ArrowStroke
  | RectStroke
  | FreehandStroke
  | TextStroke
  | MeasureStroke

/** Default palette — high-contrast, screenshot-friendly. */
export const MARKUP_COLORS = [
  '#ef4444', // red-500 - default
  '#f97316', // orange-500
  '#eab308', // yellow-500
  '#22c55e', // green-500
  '#3b82f6', // blue-500
  '#a855f7', // purple-500
  '#000000',
  '#ffffff',
] as const

export const DEFAULT_STROKE_WIDTH = 4
export const DEFAULT_FONT_SIZE = 22

/** Render an entire stroke list onto the canvas, on top of the
 *  supplied background image. The render is destructive: the
 *  canvas is fully repainted on each call so undo / redo / reorder
 *  is a single function reference change in the caller. */
export function renderMarkup(
  ctx: CanvasRenderingContext2D,
  background: HTMLImageElement | HTMLCanvasElement | null,
  strokes: ReadonlyArray<Stroke>,
): void {
  const { canvas } = ctx
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  if (background) {
    ctx.drawImage(background, 0, 0, canvas.width, canvas.height)
  }
  for (const stroke of strokes) {
    drawStroke(ctx, stroke)
  }
}

/** Dispatch one stroke to its tool-specific renderer. Exported so
 *  tests can hit each branch directly. */
export function drawStroke(
  ctx: CanvasRenderingContext2D,
  stroke: Stroke,
): void {
  if (stroke.type === 'arrow') return drawArrow(ctx, stroke)
  if (stroke.type === 'rect') return drawRect(ctx, stroke)
  if (stroke.type === 'freehand') return drawFreehand(ctx, stroke)
  if (stroke.type === 'text') return drawText(ctx, stroke)
  if (stroke.type === 'measure') return drawMeasure(ctx, stroke)
}

/** Fill a triangular arrowhead whose tip sits at (tipX, tipY), pointing
 *  back along `angle` (the direction the shaft travels toward the tip).
 *  Shared by the single-headed arrow and the double-headed dimension. */
function fillArrowHead(
  ctx: CanvasRenderingContext2D,
  tipX: number,
  tipY: number,
  angle: number,
  headLen: number,
): void {
  const a1 = angle - Math.PI / 7
  const a2 = angle + Math.PI / 7
  ctx.beginPath()
  ctx.moveTo(tipX, tipY)
  ctx.lineTo(tipX - headLen * Math.cos(a1), tipY - headLen * Math.sin(a1))
  ctx.lineTo(tipX - headLen * Math.cos(a2), tipY - headLen * Math.sin(a2))
  ctx.closePath()
  ctx.fill()
}

function drawArrow(ctx: CanvasRenderingContext2D, s: ArrowStroke): void {
  const { start, end, color, width } = s
  ctx.save()
  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineWidth = width
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(start.x, start.y)
  ctx.lineTo(end.x, end.y)
  ctx.stroke()
  // Arrowhead. Length scales with stroke width so a thin arrow
  // doesn't get a giant head and a fat arrow doesn't look stubby.
  const headLen = Math.max(12, width * 3)
  const angle = Math.atan2(end.y - start.y, end.x - start.x)
  fillArrowHead(ctx, end.x, end.y, angle, headLen)
  ctx.restore()
}

function drawMeasure(ctx: CanvasRenderingContext2D, s: MeasureStroke): void {
  const { start, end, color, width, label, fontSize } = s
  ctx.save()
  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineWidth = width
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  const angle = Math.atan2(end.y - start.y, end.x - start.x)
  const headLen = Math.max(12, width * 3)
  // Perpendicular witness ticks at each end — the CAD "extension line"
  // look. Length scales with the stroke so it stays proportional.
  const tick = Math.max(10, width * 3)
  const px = Math.cos(angle + Math.PI / 2) * (tick / 2)
  const py = Math.sin(angle + Math.PI / 2) * (tick / 2)
  ctx.beginPath()
  ctx.moveTo(start.x - px, start.y - py)
  ctx.lineTo(start.x + px, start.y + py)
  ctx.moveTo(end.x - px, end.y - py)
  ctx.lineTo(end.x + px, end.y + py)
  ctx.stroke()

  // Main dimension line + an arrowhead pointing OUT at each end.
  ctx.beginPath()
  ctx.moveTo(start.x, start.y)
  ctx.lineTo(end.x, end.y)
  ctx.stroke()
  fillArrowHead(ctx, end.x, end.y, angle, headLen)
  fillArrowHead(ctx, start.x, start.y, angle + Math.PI, headLen)

  // Label — centred on the line, rotated to follow it, kept upright so
  // it never reads upside-down, with a translucent plate behind it so
  // the line doesn't strike through the text.
  if (label) {
    const mid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 }
    let rot = angle
    if (rot > Math.PI / 2) rot -= Math.PI
    if (rot < -Math.PI / 2) rot += Math.PI
    ctx.translate(mid.x, mid.y)
    ctx.rotate(rot)
    ctx.font = `bold ${fontSize}px ui-sans-serif, system-ui, -apple-system, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'alphabetic'
    const w = ctx.measureText(label).width
    const padX = 6
    const gap = tick / 2 + 4 // sit the plate just above the line
    const plateH = fontSize + 6
    ctx.fillStyle = 'rgba(255,255,255,0.88)'
    ctx.fillRect(-w / 2 - padX, -gap - plateH, w + padX * 2, plateH)
    ctx.fillStyle = color
    ctx.fillText(label, 0, -gap - 5)
  }
  ctx.restore()
}

function drawRect(ctx: CanvasRenderingContext2D, s: RectStroke): void {
  const { start, end, color, width } = s
  ctx.save()
  ctx.strokeStyle = color
  ctx.lineWidth = width
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeRect(
    Math.min(start.x, end.x),
    Math.min(start.y, end.y),
    Math.abs(end.x - start.x),
    Math.abs(end.y - start.y),
  )
  ctx.restore()
}

function drawFreehand(ctx: CanvasRenderingContext2D, s: FreehandStroke): void {
  if (s.points.length < 2) return
  ctx.save()
  ctx.strokeStyle = s.color
  ctx.lineWidth = s.width
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(s.points[0].x, s.points[0].y)
  for (let i = 1; i < s.points.length; i += 1) {
    ctx.lineTo(s.points[i].x, s.points[i].y)
  }
  ctx.stroke()
  ctx.restore()
}

function drawText(ctx: CanvasRenderingContext2D, s: TextStroke): void {
  ctx.save()
  ctx.fillStyle = s.color
  ctx.font = `bold ${s.fontSize}px ui-sans-serif, system-ui, -apple-system, sans-serif`
  ctx.textBaseline = 'top'
  // Soft drop shadow so light-on-light or dark-on-dark stays
  // readable without forcing the operator to colour-pick first.
  ctx.shadowColor = 'rgba(0,0,0,0.6)'
  ctx.shadowBlur = 3
  ctx.fillText(s.text, s.position.x, s.position.y)
  ctx.restore()
}

/** Export the current canvas as a PNG Blob. Returns ``null`` if the
 *  browser refuses (e.g. canvas tainted by cross-origin image). */
export function exportCanvasToPng(
  canvas: HTMLCanvasElement,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b), 'image/png')
    } catch {
      resolve(null)
    }
  })
}

/** Compute the canvas pixel coords for a pointer event. We multiply
 *  by the canvas / element size ratio so a stroke at the visual
 *  position the operator clicked lands on the same pixel of the
 *  exported PNG, even if the canvas is CSS-resized to fit the
 *  drawer. */
export function pointerToCanvas(
  event: { clientX: number; clientY: number },
  canvas: HTMLCanvasElement,
): Point {
  const rect = canvas.getBoundingClientRect()
  const sx = rect.width === 0 ? 1 : canvas.width / rect.width
  const sy = rect.height === 0 ? 1 : canvas.height / rect.height
  return {
    x: (event.clientX - rect.left) * sx,
    y: (event.clientY - rect.top) * sy,
  }
}

/** Pick a sensible canvas resolution for a source image. We cap at
 *  4096px on the long edge so a 12k phone screenshot doesn't
 *  hammer the GPU; the operator still sees the full image because
 *  CSS scales the canvas to the drawer width. */
export function fitCanvasToImage(
  img: HTMLImageElement,
  maxLong = 4096,
): { width: number; height: number } {
  const w = img.naturalWidth || img.width || 1
  const h = img.naturalHeight || img.height || 1
  const longEdge = Math.max(w, h)
  if (longEdge <= maxLong) return { width: w, height: h }
  const scale = maxLong / longEdge
  return {
    width: Math.round(w * scale),
    height: Math.round(h * scale),
  }
}
